import React, { useState, useEffect, useCallback, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Container,
  Box,
  Grid,
  Paper,
  InputBase,
  IconButton,
  Chip,
  Skeleton,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Alert,
  Snackbar,
  CircularProgress,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Search,
  School,
  FilterList,
  Close,
  MyLocation,
  Navigation,
} from '@mui/icons-material';
import { AuthContext } from '../../context/AuthContext';
import { useCityContext } from '../../context/CityContext';
import api from '../../utils/api';
import InstituteCard from '../../components/InstituteCard';
import DesktopNav from '../../components/DesktopNav';
import StudentBottomNav from '../../components/StudentBottomNav';
import SearchableCityDropdown from '../../components/SearchableCityDropdown';
import useGeolocation from '../../hooks/useGeolocation';
import { COURSE_OPTIONS } from '../../utils/indianCities';
import PageHeader from '../../components/shared/PageHeader';
import EmptyState from '../../components/shared/EmptyState';
import BottomSheet from '../../components/shared/BottomSheet';

function InstituteSearch() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, user } = useContext(AuthContext);
  const { detectedCity } = useCityContext();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const { coordinates, error: locationError, loading: locationLoading, requestPermission, clearLocation } = useGeolocation();

  const [institutes, setInstitutes] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [filterSheetOpen, setFilterSheetOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  const getUserInterests = () => {
    if (!user?.interested_courses) return [];
    const courses = typeof user.interested_courses === 'string'
      ? user.interested_courses.split(',').map(c => c.trim())
      : user.interested_courses;
    return courses.filter(c =>
      COURSE_OPTIONS.some(option =>
        option.toLowerCase().includes(c.toLowerCase()) ||
        c.toLowerCase().includes(option.toLowerCase())
      )
    ).map(c => {
      const match = COURSE_OPTIONS.find(option =>
        option.toLowerCase().includes(c.toLowerCase()) ||
        c.toLowerCase().includes(option.toLowerCase())
      );
      return match || c;
    });
  };

  const [filters, setFilters] = useState({
    city: '',
    includeNearby: true,
    courses: [],
    sortBy: 'name',
    useLocation: false,
  });

  const [hasAppliedUserInterests, setHasAppliedUserInterests] = useState(false);

  const fromStudentPortal = location.state?.from === 'student-portal' ||
    document.referrer.includes('/student');

  const fetchInstitutes = useCallback(async () => {
    try {
      setLoading(true);

      const params = {
        search: searchQuery || undefined,
        city: filters.city || undefined,
        includeNearby: filters.city && filters.includeNearby ? 'true' : undefined,
        sortBy: filters.sortBy,
      };

      if (filters.useLocation && coordinates) {
        params.userLat = coordinates.latitude;
        params.userLng = coordinates.longitude;
        params.sortBy = 'distance';
      }

      const response = await api.get('/institutes', { params });
      let fetchedInstitutes = response.data.data.institutes || [];

      if (filters.courses.length > 0) {
        fetchedInstitutes = fetchedInstitutes.filter(institute => {
          if (!institute.courses_offered) return false;
          return filters.courses.some(course =>
            institute.courses_offered.some(c =>
              c.toLowerCase().includes(course.toLowerCase())
            )
          );
        });
      }

      setInstitutes(fetchedInstitutes);
    } catch (err) {
      console.error('Error fetching institutes:', err);
      setSnackbar({ open: true, message: 'Failed to fetch institutes', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [searchQuery, filters, coordinates]);

  useEffect(() => {
    fetchInstitutes();
  }, [fetchInstitutes]);

  useEffect(() => {
    if (isAuthenticated && user && !hasAppliedUserInterests) {
      const userInterests = getUserInterests();
      if (userInterests.length > 0) {
        setFilters(prev => ({ ...prev, courses: userInterests }));
        setSnackbar({
          open: true,
          message: `Showing institutes matching your interests: ${userInterests.join(', ')}`,
          severity: 'info',
        });
      }
      setHasAppliedUserInterests(true);
    }
  }, [isAuthenticated, user, hasAppliedUserInterests]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleBack = () => {
    if (fromStudentPortal || isAuthenticated) {
      navigate('/student');
    } else {
      navigate(-1);
    }
  };

  const handleNearMe = () => {
    if (coordinates) {
      setFilters(prev => ({ ...prev, useLocation: false }));
      clearLocation();
      setSnackbar({ open: true, message: 'Location filter removed', severity: 'info' });
    } else {
      requestPermission();
      setFilters(prev => ({ ...prev, useLocation: true }));
    }
  };

  useEffect(() => {
    if (locationError) {
      setSnackbar({ open: true, message: locationError.message, severity: 'error' });
      setFilters(prev => ({ ...prev, useLocation: false }));
    }
  }, [locationError]);

  const clearAllFilters = useCallback(() => {
    setFilters({ city: '', includeNearby: true, courses: [], sortBy: 'name', useLocation: false });
    setSearchQuery('');
    clearLocation();
  }, [clearLocation]);

  const handleCourseToggle = useCallback((course) => {
    setFilters(prev => ({
      ...prev,
      courses: prev.courses.includes(course)
        ? prev.courses.filter(c => c !== course)
        : [...prev.courses, course],
    }));
  }, []);

  const activeFiltersCount = [
    filters.city,
    filters.useLocation,
    filters.courses.length > 0,
  ].filter(Boolean).length;

  const FilterContent = () => (
    <Box>
      {activeFiltersCount > 0 && (
        <Button size="small" onClick={clearAllFilters} sx={{ mb: 2, textTransform: 'none' }}>
          Clear All
        </Button>
      )}

      {/* Near Me */}
      <Button
        fullWidth
        variant={filters.useLocation ? 'contained' : 'outlined'}
        startIcon={locationLoading ? <CircularProgress size={20} /> : <MyLocation />}
        onClick={handleNearMe}
        disabled={locationLoading}
        sx={{ mb: 3, borderRadius: 3, py: 1.5, fontWeight: 600, textTransform: 'none' }}
      >
        {filters.useLocation ? 'Using Your Location' : 'Near Me'}
      </Button>

      {/* City */}
      <Box sx={{ mb: 3 }}>
        <SearchableCityDropdown
          value={filters.city}
          onChange={(newCity) => setFilters(prev => ({ ...prev, city: newCity }))}
          detectedCity={detectedCity}
          includeNearby={filters.includeNearby}
          onIncludeNearbyChange={(checked) => setFilters(prev => ({ ...prev, includeNearby: checked }))}
          showIncludeNearbyCheckbox
          label="City"
          placeholder="Search city..."
          fullWidth
        />
      </Box>

      {/* Sort By */}
      <FormControl fullWidth sx={{ mb: 3 }}>
        <InputLabel>Sort By</InputLabel>
        <Select
          value={filters.sortBy}
          label="Sort By"
          onChange={(e) => setFilters(prev => ({ ...prev, sortBy: e.target.value }))}
        >
          <MenuItem value="name">Name</MenuItem>
          <MenuItem value="distance" disabled={!filters.useLocation}>
            Distance {!filters.useLocation && '(Enable Near Me)'}
          </MenuItem>
          <MenuItem value="rating">Rating</MenuItem>
        </Select>
      </FormControl>

      {/* Courses */}
      <Typography variant="subtitle2" fontWeight={600} gutterBottom>
        Courses Offered
      </Typography>
      <FormGroup>
        {COURSE_OPTIONS.map((course) => (
          <FormControlLabel
            key={course}
            control={
              <Checkbox
                checked={filters.courses.includes(course)}
                onChange={() => handleCourseToggle(course)}
              />
            }
            label={course}
          />
        ))}
      </FormGroup>

      {isMobile && (
        <Button
          fullWidth
          variant="contained"
          onClick={() => setFilterSheetOpen(false)}
          sx={{ mt: 2, borderRadius: 3, py: 1.5, fontWeight: 600, textTransform: 'none' }}
        >
          Show {institutes.length} {institutes.length === 1 ? 'Institute' : 'Institutes'}
        </Button>
      )}
    </Box>
  );

  return (
    <Box sx={{ minHeight: '100vh', pb: { xs: 10, md: 4 } }}>
      <DesktopNav />

      {/* Page Header */}
      <PageHeader
        title="Find institutes"
        subtitle={`${institutes.length} ${institutes.length === 1 ? 'institute' : 'institutes'} found`}
        backTo={handleBack}
        sticky
        actions={
          isMobile ? (
            <Box sx={{ position: 'relative' }}>
              <IconButton
                onClick={() => setFilterSheetOpen(true)}
                aria-label="Open filters"
                color="inherit"
              >
                <FilterList />
              </IconButton>
              {activeFiltersCount > 0 && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 4,
                    right: 4,
                    width: 16,
                    height: 16,
                    borderRadius: '50%',
                    bgcolor: 'error.main',
                    color: 'common.white',
                    fontSize: '0.6rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 700,
                    pointerEvents: 'none',
                  }}
                >
                  {activeFiltersCount}
                </Box>
              )}
            </Box>
          ) : null
        }
      />

      {/* Search bar */}
      <Box sx={{ bgcolor: 'background.paper', px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
        <Paper
          elevation={0}
          sx={{
            display: 'flex',
            alignItems: 'center',
            px: 2,
            borderRadius: 3,
            border: 1,
            borderColor: 'divider',
            bgcolor: 'background.subtle',
          }}
        >
          <Search sx={{ color: 'text.secondary', mr: 1, flexShrink: 0 }} />
          <InputBase
            fullWidth
            placeholder="Search by name, city, or course..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ py: 1.25, fontSize: '0.95rem' }}
            inputProps={{ 'aria-label': 'Search institutes' }}
          />
          {searchQuery && (
            <IconButton size="small" onClick={() => setSearchQuery('')} aria-label="Clear search">
              <Close fontSize="small" />
            </IconButton>
          )}
          {!isMobile && (
            <IconButton
              onClick={handleNearMe}
              disabled={locationLoading}
              aria-label="Use my location"
              size="small"
              color={filters.useLocation ? 'primary' : 'default'}
              sx={{ ml: 0.5 }}
            >
              {locationLoading ? <CircularProgress size={18} /> : <MyLocation fontSize="small" />}
            </IconButton>
          )}
        </Paper>

        {/* Active filter chips */}
        {activeFiltersCount > 0 && (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1.5 }}>
            {filters.useLocation && (
              <Chip
                icon={<Navigation />}
                label="Near Me"
                onDelete={() => { setFilters(prev => ({ ...prev, useLocation: false })); clearLocation(); }}
                color="primary"
                size="small"
              />
            )}
            {filters.city && (
              <Chip
                label={`City: ${filters.city}`}
                onDelete={() => setFilters(prev => ({ ...prev, city: '' }))}
                size="small"
              />
            )}
            {filters.courses.map((course) => (
              <Chip
                key={course}
                label={course}
                onDelete={() => setFilters(prev => ({ ...prev, courses: prev.courses.filter(c => c !== course) }))}
                size="small"
              />
            ))}
          </Box>
        )}
      </Box>

      {/* Content */}
      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Grid container spacing={3}>
          {/* Desktop sidebar filters */}
          {!isMobile && (
            <Grid item md={3}>
              <Paper sx={{ borderRadius: 3, position: 'sticky', top: 80, maxHeight: 'calc(100vh - 100px)', overflowY: 'auto' }}>
                <Box sx={{ p: 2.5 }}>
                  <Typography variant="subtitle1" fontWeight={700} mb={2}>
                    Filters
                  </Typography>
                  <FilterContent />
                </Box>
              </Paper>
            </Grid>
          )}

          {/* Institute grid */}
          <Grid item xs={12} md={isMobile ? 12 : 9}>
            {loading ? (
              <Grid container spacing={2.5}>
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <Grid item xs={12} sm={6} md={4} key={i}>
                    <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 3 }} />
                  </Grid>
                ))}
              </Grid>
            ) : institutes.length === 0 ? (
              <EmptyState
                icon={<School />}
                title="No institutes match your filters"
                description="Try broadening your search criteria or clearing all filters"
                actionLabel="Clear All Filters"
                onAction={clearAllFilters}
              />
            ) : (
              <Grid container spacing={2.5}>
                {institutes.map((institute) => (
                  <Grid item xs={12} sm={6} md={4} key={institute.id}>
                    <InstituteCard institute={institute} />
                  </Grid>
                ))}
              </Grid>
            )}

            {!loading && institutes.length > 0 && (
              <Box sx={{ textAlign: 'center', mt: 4 }}>
                <Typography variant="body2" color="text.secondary">
                  Showing {institutes.length} {institutes.length === 1 ? 'institute' : 'institutes'}
                </Typography>
              </Box>
            )}
          </Grid>
        </Grid>
      </Container>

      {/* Mobile filter BottomSheet */}
      <BottomSheet
        open={filterSheetOpen}
        onClose={() => setFilterSheetOpen(false)}
        title="Filters"
      >
        <FilterContent />
      </BottomSheet>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {isAuthenticated && <StudentBottomNav />}
    </Box>
  );
}

export default InstituteSearch;
